import type { Thing, WebPage, WithContext } from 'schema-dts';
import type { Question } from '@/entities/question';
import type { AppBreadcrumbs } from '@/shared/components/shared/Breadcrumbs';
import { generateFaqPageJsonLd } from '@/shared/lib/utils/faq-json-ld';
import {
    type SchemaContacts,
    generateBreadcrumbListJsonLd,
    oneLine,
    schemaIdRef,
    stripHtml,
} from '@/shared/lib/utils/schema-common';
import { getSchemaIds, schemaPageUrl } from '@/shared/lib/utils/schema-ids';

export function composeSchemaGraph(input: {
    nodes: WithContext<Thing>[];
    breadcrumbs?: AppBreadcrumbs | null;
    faqQuestions?: Question[] | null;
    faqPageUrl?: string;
}): WithContext<Thing>[] {
    const graph = [...input.nodes];
    if (input.breadcrumbs && input.breadcrumbs.length > 0) {
        graph.push(generateBreadcrumbListJsonLd(input.breadcrumbs) as WithContext<Thing>);
    }
    if (input.faqQuestions && input.faqPageUrl) {
        const faq = generateFaqPageJsonLd(input.faqQuestions, input.faqPageUrl);
        if (faq) graph.push(faq);
    }
    return graph;
}

export type GenerateWebPageJsonLdInput = {
    name: string;
    description?: string | null;
    pagePath: string;
    breadcrumbs?: AppBreadcrumbs | null;
    faqQuestions?: Question[] | null;
    contacts?: SchemaContacts | null;
};

export function generateWebPageJsonLd({
    name,
    description,
    pagePath,
    breadcrumbs,
    faqQuestions,
}: GenerateWebPageJsonLdInput): WithContext<Thing>[] {
    const ids = getSchemaIds();
    const pageUrl = schemaPageUrl(pagePath);
    const descriptionText = description ? oneLine(stripHtml(description)) : '';
    const webPage: WithContext<WebPage> = {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${pageUrl.replace(/\/$/, '')}/#webpage`,
        name: oneLine(stripHtml(name)),
        url: pageUrl,
        ...(descriptionText ? { description: descriptionText } : {}),
        isPartOf: schemaIdRef(ids.website) as WebPage['isPartOf'],
        publisher: schemaIdRef(ids.organization) as WebPage['publisher'],
    };

    return composeSchemaGraph({
        nodes: [webPage as WithContext<Thing>],
        breadcrumbs,
        faqQuestions,
        faqPageUrl: pageUrl,
    });
}
